import React, { useEffect, useState, useMemo, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import "./listAppointmentConfig.css";
import { useDispatch, useSelector } from "react-redux";
import {
    getHomeVisitConfigsList,
    viewHomeVisitConfig,
    updateHomeVisitConfig,
    deleteHomeVisitConfig,
    addHomeVisitConfig
} from "../../Redux/Actions/appointmentConfigAction";
import toast from "react-hot-toast";

const DEFAULT_NEW_CONFIG = {
    agent_acceptance_time_diff: 0,
    cancellation_by_user_charge: 0,
    cancellation_by_admin_charge: 0,
    appointment_booking_charge: [
        { name: "", count: 0, charge: 0, currency: "INR" }
    ],
};

const AppointmentConfigView = () => {
    const dispatch = useDispatch();
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);

    const [addMode, setAddMode] = useState(false);
    const [addData, setAddData] = useState(DEFAULT_NEW_CONFIG);

    const { admin } = useSelector((state) => state.admin || {});
    const { viewConfig, loading } = useSelector((state) => state.appointmentConfig || {});
    const { configsList } = useSelector((state) => state.appointmentConfig || {});

    let configId = configsList?.data?.data[0]?._id

    const { canEdit } = useMemo(() => {
        const codes = admin?.data?.access_code || [];
        return {
            canEdit: Array.isArray(codes) && codes.includes("edit"),
        };
    }, [admin]);

    useEffect(() => {
        dispatch(getHomeVisitConfigsList({ page: 1, limit: 10 }));
    }, [dispatch]);

    useEffect(() => {
        if (configId) {
            dispatch(viewHomeVisitConfig(configId));
        }
    }, [configId, dispatch]);

    const config = viewConfig?.data?.data ?? null;
    const isDataNull = !loading && config === null;

    const handleAddInputChange = useCallback((key, value) => {
        const processedValue = [
            "agent_acceptance_time_diff",
            "cancellation_by_user_charge",
            "cancellation_by_admin_charge",
        ].includes(key)
            ? Number(value) || 0
            : value;

        setAddData((prev) => ({ ...prev, [key]: processedValue }));
    }, []);

    const handleAddChargeInputChange = useCallback((index, field, value) => {
        const processedValue = ["count", "charge"].includes(field)
            ? Number(value) || 0
            : value;

        setAddData((prev) => {
            const newCharges = [...(prev.appointment_booking_charge || [])];
            newCharges[index] = { ...newCharges[index], [field]: processedValue };
            return { ...prev, appointment_booking_charge: newCharges };
        });
    }, []);

    const handleAddNewCharge = useCallback(() => {
        setAddData((prev) => ({
            ...prev,
            appointment_booking_charge: [
                ...(prev.appointment_booking_charge || []),
                { name: "", count: 0, charge: 0, currency: "INR" },
            ],
        }));
    }, []);

    const handleRemoveNewCharge = useCallback((index) => {
        setAddData((prev) => {
            const newCharges = [...(prev.appointment_booking_charge || [])];
            newCharges.splice(index, 1);
            return { ...prev, appointment_booking_charge: newCharges };
        });
    }, []);

    const handleCreate = useCallback(async () => {
        try {
            const cleanCharges = (addData.appointment_booking_charge || []).map(
                ({ _id, ...rest }) => rest
            );

            const payload = {
                user_id: admin?.data?._id,
                appointment_booking_charge: cleanCharges,
                agent_acceptance_time_diff: Number(addData.agent_acceptance_time_diff) || 0,
                cancellation_by_user_charge: Number(addData.cancellation_by_user_charge) || 0,
                cancellation_by_admin_charge: Number(addData.cancellation_by_admin_charge) || 0,
            };

            const result = await dispatch(addHomeVisitConfig(payload));

            const newConfigId = result?.payload?.data?._id || result?.data?._id;

            if (newConfigId) {
                await dispatch(viewHomeVisitConfig(newConfigId));
                // toast.success("Config created successfully!");

                await dispatch(getHomeVisitConfigsList({ page: 1, limit: 10 }));
            }

            setAddMode(false);
            setAddData(DEFAULT_NEW_CONFIG);

        } catch (error) {
            toast.error(error?.message || "Failed to create config");
        }
    }, [dispatch, addData, admin?.data?._id]);


    const handleEditClick = useCallback(() => {
        if (config) {
            setEditData({
                ...config,
                appointment_booking_charge: [
                    ...(config.appointment_booking_charge || []),
                ],
            });
            setEditing(true);
        }
    }, [config]);

    const handleInputChange = useCallback((key, value) => {
        const processedValue = [
            "agent_acceptance_time_diff",
            "cancellation_by_user_charge",
            "cancellation_by_admin_charge",
        ].includes(key)
            ? Number(value) || 0
            : value;

        setEditData((prev) => ({ ...prev, [key]: processedValue }));
    }, []);

    const handleChargeInputChange = useCallback((index, field, value) => {
        const processedValue = ["count", "charge"].includes(field)
            ? Number(value) || 0
            : value;

        setEditData((prev) => {
            const newCharges = [...(prev.appointment_booking_charge || [])];
            newCharges[index] = { ...newCharges[index], [field]: processedValue };
            return { ...prev, appointment_booking_charge: newCharges };
        });
    }, []);

    const handleAddCharge = useCallback(() => {
        setEditData((prev) => ({
            ...prev,
            appointment_booking_charge: [
                ...(prev.appointment_booking_charge || []),
                { name: "", count: 0, charge: 0, currency: "INR" },
            ],
        }));
    }, []);

    const handleDeleteCharge = useCallback((index) => {
        setEditData((prev) => {
            const newCharges = [...(prev.appointment_booking_charge || [])];
            newCharges.splice(index, 1);
            return { ...prev, appointment_booking_charge: newCharges };
        });
    }, []);

    const handleSave = useCallback(() => {
        const cleanCharges = (editData.appointment_booking_charge || []).map(
            ({ _id, ...rest }) => rest
        );

        const payload = {
            user_id: admin?.data?._id,
            appointment_booking_charge: cleanCharges,
            agent_acceptance_time_diff:
                editData.agent_acceptance_time_diff !== undefined
                    ? Number(editData.agent_acceptance_time_diff)
                    : undefined,
            cancellation_by_user_charge:
                editData.cancellation_by_user_charge !== undefined
                    ? Number(editData.cancellation_by_user_charge)
                    : undefined,
            cancellation_by_admin_charge:
                editData.cancellation_by_admin_charge !== undefined
                    ? Number(editData.cancellation_by_admin_charge)
                    : undefined,
        };

        Object.keys(payload).forEach(
            (key) => payload[key] === undefined && delete payload[key]
        );

        dispatch(updateHomeVisitConfig(configId, payload))
            .then(() => {
                toast.success("Config updated successfully!");
                setEditing(false);
                dispatch(viewHomeVisitConfig(configId));
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to update config");
            });
    }, [dispatch, configId, editData, admin?.data?._id]);

    const handleDelete = useCallback(() => {
        dispatch(deleteHomeVisitConfig(configId))
            .then(() => {
                setDeleteModal(false);
                dispatch(viewHomeVisitConfig(configId));
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to delete config");
                setDeleteModal(false);
            });
    }, [dispatch, configId]);

    const formatLabel = useCallback((key) => {
        return key
            .replace(/_/g, " ")
            .replace(/\w\S*/g, (txt) =>
                txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
            );
    }, []);


    if (loading) {
        return (
            <MainLayout>
                <div className="acv-page">
                    <div className="acv-loading">
                        <div className="acv-spinner" />
                        <span>Loading configuration...</span>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (isDataNull) {
        const newCharges = addData.appointment_booking_charge || [];

        return (
            <MainLayout>
                <div className="acv-page">
                    <div className="acv-header">
                        <div className="acv-header-left">
                            <div className="acv-breadcrumb">
                                <span>Settings</span>
                                <span className="acv-breadcrumb-sep">/</span>
                                <span className="acv-breadcrumb-current">Appointment Config</span>
                            </div>
                            <h1 className="acv-title">Create Configuration</h1>
                            <p className="acv-subtitle">
                                No configuration exists yet. Fill in the details below to get started.
                            </p>
                        </div>

                        {addMode && (
                            <div className="acv-header-actions">
                                <button
                                    className="acv-btn acv-btn-ghost"
                                    onClick={() => {
                                        setAddMode(false);
                                        setAddData(DEFAULT_NEW_CONFIG);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="acv-btn acv-btn-primary"
                                    onClick={handleCreate}
                                >
                                    <span className="acv-btn-icon">✓</span>
                                    Create Config
                                </button>
                            </div>
                        )}
                    </div>

                    {!addMode ? (
                        <div className="acv-section" style={{ marginTop: 0 }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "64px 24px",
                                    gap: "16px",
                                    textAlign: "center",
                                }}
                            >
                                <div className="acv-empty-icon" style={{ opacity: 0.5 }}>⚙</div>
                                <p
                                    style={{
                                        fontSize: "15px",
                                        color: "var(--acv-text-secondary)",
                                        margin: 0,
                                        maxWidth: 360,
                                    }}
                                >
                                    No appointment configuration found. Create one to define booking
                                    charges and agent acceptance rules.
                                </p>
                                <button
                                    className="acv-btn acv-btn-primary"
                                    style={{ marginTop: 8 }}
                                    onClick={() => setAddMode(true)}
                                >
                                    <span className="acv-btn-icon">+</span>
                                    Add Config
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="acv-edit-banner">
                                <span className="acv-edit-dot" />
                                Fill in all fields and click{" "}
                                <strong>Create Config</strong> to save.
                            </div>

                            <div className="acv-content">
                                <div className="acv-section">
                                    <div className="acv-section-header">
                                        <div className="acv-section-tag">Charges</div>
                                        <h2 className="acv-section-title">
                                            Appointment Booking Charges
                                        </h2>
                                    </div>

                                    <div className="acv-charges-grid">
                                        {newCharges.map((charge, index) => (
                                            <div
                                                key={`new-charge-${index}`}
                                                className="acv-charge-card acv-charge-card--editing"
                                            >
                                                <div className="acv-charge-index">{index + 1}</div>

                                                {newCharges.length > 1 && (
                                                    <button
                                                        className="acv-charge-delete"
                                                        onClick={() => handleRemoveNewCharge(index)}
                                                        title="Remove charge tier"
                                                    >
                                                        ×
                                                    </button>
                                                )}

                                                <div className="acv-charge-row">
                                                    <div className="acv-charge-field">
                                                        <label className="acv-field-label">Name</label>
                                                        <input
                                                            className="acv-input"
                                                            placeholder="e.g. Standard Visit"
                                                            value={charge.name}
                                                            onChange={(e) =>
                                                                handleAddChargeInputChange(
                                                                    index,
                                                                    "name",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                    <div className="acv-charge-field acv-charge-field--sm">
                                                        <label className="acv-field-label">Count</label>
                                                        <input
                                                            className="acv-input"
                                                            type="number"
                                                            min="0"
                                                            value={charge.count}
                                                            onChange={(e) =>
                                                                handleAddChargeInputChange(
                                                                    index,
                                                                    "count",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                    <div className="acv-charge-field">
                                                        <label className="acv-field-label">Charge</label>
                                                        <div className="acv-input-prefix-wrap">
                                                            <span className="acv-input-prefix">₹</span>
                                                            <input
                                                                className="acv-input acv-input--prefixed"
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={charge.charge}
                                                                onChange={(e) =>
                                                                    handleAddChargeInputChange(
                                                                        index,
                                                                        "charge",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="acv-charge-field acv-charge-field--sm">
                                                        <label className="acv-field-label">Currency</label>
                                                        <input
                                                            className="acv-input"
                                                            value={charge.currency}
                                                            onChange={(e) =>
                                                                handleAddChargeInputChange(
                                                                    index,
                                                                    "currency",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="acv-add-charge-wrapper">
                                        <button
                                            className="acv-btn acv-btn-secondary acv-add-charge-btn"
                                            onClick={handleAddNewCharge}
                                        >
                                            <span className="acv-btn-icon">+</span>
                                            Add Charge Tier
                                        </button>
                                    </div>
                                </div>
                                <div className="acv-section">
                                    <div className="acv-section-header">
                                        <div className="acv-section-tag">General</div>
                                        <h2 className="acv-section-title">
                                            Configuration Parameters
                                        </h2>
                                    </div>

                                    <div className="acv-fields-grid">
                                        {[
                                            "agent_acceptance_time_diff",
                                            "cancellation_by_user_charge",
                                            "cancellation_by_admin_charge",
                                        ].map((key) => (
                                            <div key={key} className="acv-field-group">
                                                <label className="acv-field-label">
                                                    {formatLabel(key)}
                                                </label>
                                                <input
                                                    className="acv-input"
                                                    type="number"
                                                    min="0"
                                                    value={addData[key] ?? 0}
                                                    onChange={(e) =>
                                                        handleAddInputChange(key, e.target.value)
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 10,
                                        paddingTop: 4,
                                    }}
                                >
                                    <button
                                        className="acv-btn acv-btn-ghost"
                                        onClick={() => {
                                            setAddMode(false);
                                            setAddData(DEFAULT_NEW_CONFIG);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="acv-btn acv-btn-primary"
                                        onClick={handleCreate}
                                    >
                                        <span className="acv-btn-icon">✓</span>
                                        Create Config
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </MainLayout>
        );
    }

    const charges = editing
        ? editData.appointment_booking_charge || []
        : config.appointment_booking_charge || [];

    return (
        <MainLayout>
            <div className="acv-page">
                {deleteModal && (
                    <div
                        className="acv-modal-overlay"
                        onClick={() => setDeleteModal(false)}
                    >
                        <div
                            className="acv-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="acv-modal-icon">🗑</div>
                            <h3 className="acv-modal-title">Delete Configuration</h3>
                            <p className="acv-modal-desc">
                                This action cannot be undone. The configuration will be
                                permanently removed.
                            </p>
                            <div className="acv-modal-actions">
                                <button
                                    className="acv-btn acv-btn-ghost"
                                    onClick={() => setDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="acv-btn acv-btn-danger"
                                    onClick={handleDelete}
                                >
                                    Yes, Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="acv-header">
                    <div className="acv-header-left">
                        <div className="acv-breadcrumb">
                            <span>Settings</span>
                            <span className="acv-breadcrumb-sep">/</span>
                            <span className="acv-breadcrumb-current">
                                Appointment Config
                            </span>
                        </div>
                        <h1 className="acv-title">Appointment Configuration</h1>
                        <p className="acv-subtitle">
                            Manage home visit appointment settings and booking charges
                        </p>
                    </div>

                    <div className="acv-header-actions">
                        {editing ? (
                            <>
                                <button
                                    className="acv-btn acv-btn-ghost"
                                    onClick={() => setEditing(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="acv-btn acv-btn-primary"
                                    onClick={handleSave}
                                >
                                    <span className="acv-btn-icon">✓</span>
                                    Save Changes
                                </button>
                            </>
                        ) : (
                            <>
                                {canEdit && (
                                    <button
                                        className="acv-btn acv-btn-secondary"
                                        onClick={handleEditClick}
                                    >
                                        <span className="acv-btn-icon">✎</span>
                                        Edit Config
                                    </button>
                                )}
                                {canEdit && (
                                    <button
                                        className="acv-btn acv-btn-danger-outline"
                                        onClick={() => setDeleteModal(true)}
                                    >
                                        <span className="acv-btn-icon">🗑</span>
                                        Delete
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {editing && (
                    <div className="acv-edit-banner">
                        <span className="acv-edit-dot" />
                        You are currently in edit mode — make changes and click{" "}
                        <strong>Save Changes</strong> to apply.
                    </div>
                )}

                <div className="acv-content">
                    <div className="acv-section">
                        <div className="acv-section-header">
                            <div className="acv-section-tag">Charges</div>
                            <h2 className="acv-section-title">
                                Appointment Booking Charges
                            </h2>
                        </div>

                        <div className="acv-charges-grid">
                            {charges.map((charge, index) => (
                                <div
                                    key={charge._id || `charge-${index}`}
                                    className={`acv-charge-card ${editing ? "acv-charge-card--editing" : ""}`}
                                >
                                    <div className="acv-charge-index">
                                        {editing ? `${index + 1}` : `#${index + 1}`}
                                    </div>

                                    {editing && (
                                        <button
                                            className="acv-charge-delete"
                                            onClick={() => handleDeleteCharge(index)}
                                            title="Remove charge tier"
                                        >
                                            ×
                                        </button>
                                    )}

                                    <div className="acv-charge-row">
                                        <div className="acv-charge-field">
                                            <label className="acv-field-label">Name</label>
                                            {editing ? (
                                                <input
                                                    className="acv-input"
                                                    value={
                                                        editData
                                                            .appointment_booking_charge?.[index]
                                                            ?.name ?? charge.name ?? ""
                                                    }
                                                    onChange={(e) =>
                                                        handleChargeInputChange(
                                                            index,
                                                            "name",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <span className="acv-field-value">
                                                    {charge.name}
                                                </span>
                                            )}
                                        </div>

                                        <div className="acv-charge-field acv-charge-field--sm">
                                            <label className="acv-field-label">Count</label>
                                            {editing ? (
                                                <input
                                                    className="acv-input"
                                                    type="number"
                                                    min="0"
                                                    value={
                                                        editData
                                                            .appointment_booking_charge?.[index]
                                                            ?.count ?? charge.count ?? 0
                                                    }
                                                    onChange={(e) =>
                                                        handleChargeInputChange(
                                                            index,
                                                            "count",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <span className="acv-field-value acv-badge">
                                                    {charge.count}
                                                </span>
                                            )}
                                        </div>

                                        <div className="acv-charge-field">
                                            <label className="acv-field-label">Charge</label>
                                            {editing ? (
                                                <div className="acv-input-prefix-wrap">
                                                    <span className="acv-input-prefix">₹</span>
                                                    <input
                                                        className="acv-input acv-input--prefixed"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={
                                                            editData
                                                                .appointment_booking_charge?.[index]
                                                                ?.charge ?? charge.charge ?? 0
                                                        }
                                                        onChange={(e) =>
                                                            handleChargeInputChange(
                                                                index,
                                                                "charge",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <span className="acv-field-value acv-price">
                                                    ₹{charge.charge}
                                                </span>
                                            )}
                                        </div>

                                        <div className="acv-charge-field acv-charge-field--sm">
                                            <label className="acv-field-label">Currency</label>
                                            {editing ? (
                                                <input
                                                    className="acv-input"
                                                    value={
                                                        editData
                                                            .appointment_booking_charge?.[index]
                                                            ?.currency ?? charge.currency ?? "INR"
                                                    }
                                                    onChange={(e) =>
                                                        handleChargeInputChange(
                                                            index,
                                                            "currency",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <span className="acv-field-value acv-currency-tag">
                                                    {charge.currency}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {editing && (
                            <div className="acv-add-charge-wrapper">
                                <button
                                    className="acv-btn acv-btn-secondary acv-add-charge-btn"
                                    onClick={handleAddCharge}
                                >
                                    <span className="acv-btn-icon">+</span>
                                    Add Charge Tier
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="acv-section">
                        <div className="acv-section-header">
                            <div className="acv-section-tag">General</div>
                            <h2 className="acv-section-title">
                                Configuration Parameters
                            </h2>
                        </div>

                        <div className="acv-fields-grid">
                            {Object.entries(config).map(([key, val]) => {
                                const skipFields = [
                                    "_id",
                                    "appointment_booking_charge",
                                ];
                                if (skipFields.includes(key)) return null;

                                const auditFields = [
                                    "is_deleted",
                                    "created_by",
                                    "updated_by",
                                    "deleted_by",
                                    "deleted_at",
                                    "created_at",
                                    "updated_at",
                                ];
                                const isAudit = auditFields.includes(key);

                                return (
                                    <div
                                        key={key}
                                        className={`acv-field-group ${isAudit ? "acv-field-group--audit" : ""}`}
                                    >
                                        <label className="acv-field-label">
                                            {formatLabel(key)}
                                            {isAudit && (
                                                <span className="acv-field-audit-tag">
                                                    read-only
                                                </span>
                                            )}
                                        </label>
                                        {editing && !isAudit ? (
                                            <input
                                                className="acv-input"
                                                type={
                                                    [
                                                        "agent_acceptance_time_diff",
                                                        "cancellation_by_user_charge",
                                                        "cancellation_by_admin_charge",
                                                    ].includes(key)
                                                        ? "number"
                                                        : "text"
                                                }
                                                min="0"
                                                value={editData[key] ?? val ?? ""}
                                                onChange={(e) =>
                                                    handleInputChange(key, e.target.value)
                                                }
                                            />
                                        ) : (
                                            <div
                                                className={`acv-field-readonly ${isAudit ? "acv-field-readonly--audit" : ""}`}
                                            >
                                                {typeof val === "boolean" ? (
                                                    <span
                                                        className={`acv-bool-tag ${val ? "acv-bool-tag--true" : "acv-bool-tag--false"}`}
                                                    >
                                                        {val ? "True" : "False"}
                                                    </span>
                                                ) : val !== null && val !== undefined ? (
                                                    val
                                                ) : (
                                                    "—"
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AppointmentConfigView;